/* GBMC v7 — published-product discovery. No app, external script or API key.
   Liquid supplies the classification and Shopify-formatted prices. The first
   250 products are server-rendered. Additional pages use the read-only Section
   Rendering API; partial results never replace a complete catalog silently. */
(() => {
  'use strict';
  if (window.GbmDiscovery) return;
  const stores = new Map();
  const $$ = (root, selector) => [...root.querySelectorAll(selector)];
  const key = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/['’‘]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').replace('lion-s-mane', 'lions-mane');
  const scope = url => new URL(url, location.origin).pathname.replace(/\/$/, '');
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const relativeURL = url => {
    const target = new URL(url, location.origin);
    if (target.origin !== location.origin) throw new Error('Catalog link is not a storefront URL.');
    return target.pathname + target.search + target.hash;
  };
  const mushroomURL = (url, mushroom) => {
    const target = new URL(url, location.origin);
    target.searchParams.set('mushroom', mushroom);
    return relativeURL(target);
  };
  const arrow = () => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '1.6');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(svg.namespaceURI, 'path');
    path.setAttribute('d', 'M4 12h15m-6-6 6 6-6 6'); svg.append(path); return svg;
  };
  function validSeed(seed) {
    return seed && typeof seed.url === 'string' && Array.isArray(seed.products)
      && Number.isInteger(seed.page) && seed.page >= 1 && Number.isInteger(seed.pages) && seed.pages >= 0;
  }
  function register(root = document, reset = false) {
    $$(root, 'script[data-gbm-catalog-seed]').forEach(script => {
      try {
        const seed = JSON.parse(script.textContent);
        if (!validSeed(seed)) return;
        const id = scope(seed.url);
        let store = stores.get(id);
        if (!store || reset) {
          store = {url: seed.url, pages: new Map(), count: Math.max(1, seed.pages), preferred: seed.preferred || '',
            pageParam: seed.pageParam || 'page', promise: null};
          stores.set(id, store);
        }
        store.pages.set(seed.page, seed.products);
        store.count = Math.max(store.count, seed.pages);
      } catch (error) { console.warn('Catalog snapshot could not be read; server-rendered links remain available.', error); }
    });
  }
  async function fetchPage(store, page) {
    const url = new URL(store.url, location.origin);
    const current = new URL(location.href);
    // Carry only preview context, not a reserved format parameter or page view.
    ['preview_theme_id', '_fd', 'pb'].forEach(param => {
      if (current.searchParams.has(param)) url.searchParams.set(param, current.searchParams.get(param));
    });
    url.searchParams.set('section_id', 'gbm-catalog-feed');
    url.searchParams.set(store.pageParam, String(page));
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(url, {credentials: 'same-origin', signal: controller.signal, headers: {Accept: 'text/html'}});
      if (!response.ok) throw new Error(`Catalog page returned ${response.status}.`);
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const script = document.querySelector('script[data-gbm-catalog-seed]');
      if (!script) throw new Error('Catalog section was not returned.');
      const seed = JSON.parse(script.textContent);
      if (!validSeed(seed) || seed.page !== page || scope(seed.url) !== scope(store.url)) throw new Error('Unexpected catalog page.');
      store.count = Math.max(1, seed.pages);
      store.pages.set(page, seed.products);
    } finally { clearTimeout(timeout); }
  }
  function groupProducts(products, preferred = '') {
    const groups = new Map();
    products.forEach(product => {
      if (!product || !product.key || !Array.isArray(product.choices)) return;
      let group = groups.get(product.key);
      if (!group) {
        group = {key: product.key, name: product.name, products: [], choices: [], selected: {}};
        groups.set(product.key, group);
      }
      group.products.push(product);
      product.choices.forEach(choice => {
        const candidate = {...choice, productId: product.id, title: product.title};
        group.choices.push(candidate);
        const previous = group.selected[choice.format];
        if (!previous || (!previous.available && candidate.available)) group.selected[choice.format] = candidate;
      });
    });
    const order = preferred.split(',').map(key);
    return [...groups.values()].map(group => {
      group.formats = ['capsules', 'powder', 'other'].filter(format => group.selected[format]);
      group.primary = group.selected.capsules || group.selected.powder || group.selected.other || group.choices[0];
      group.lowest = group.choices.reduce((best, choice) => !best || choice.price < best.price ? choice : best, null);
      group.available = group.choices.some(choice => choice.available);
      group.extras = group.choices.filter(choice => group.selected[choice.format] !== choice);
      return group;
    }).sort((a, b) => {
      const ai = order.indexOf(a.key), bi = order.indexOf(b.key);
      return (ai < 0 ? order.length : ai) - (bi < 0 ? order.length : bi);
    });
  }
  async function ready(url) {
    register();
    const id = scope(url);
    let store = stores.get(id);
    if (!store) {
      store = {url, pages: new Map(), count: 1, preferred: '', pageParam: 'page', promise: null};
      stores.set(id, store);
    }
    if (!store.promise) {
      store.promise = (async () => {
        // Shopify permits access through item 25,000. Bounded, sequential page
        // requests avoid a burst of background connections on larger ranges.
        for (let page = 1; page <= store.count; page++) {
          if (page > 100) throw new Error('Catalog exceeds the supported discovery window.');
          if (!store.pages.has(page)) await fetchPage(store, page);
        }
        const unique = new Map();
        [...store.pages].sort((a, b) => a[0] - b[0]).forEach(([page, products]) => {
          if (page <= store.count) products.forEach(product => unique.set(product.id, product));
        });
        const products = [...unique.values()];
        return {products, groups: groupProducts(products, store.preferred), url: store.url};
      })().catch(error => { store.promise = null; throw error; });
    }
    return store.promise;
  }
  function imageOrPlaceholder(src, name, className) {
    if (!src) {
      const empty = element('div', 'gbm-image-empty'); empty.append(element('span', '', name)); return empty;
    }
    const image = element('img', className); image.src = src; image.alt = name;
    image.loading = 'lazy'; image.decoding = 'async'; image.width = 900; image.height = 1125;
    return image;
  }
  function makeCard(group) {
    const primary = group.primary;
    if (!primary) return null;
    const card = element('article', 'gbm-shop-card');
    card.dataset.gbmCard = ''; card.dataset.mushroom = group.key; card.dataset.species = group.name;
    card.dataset.formats = group.formats.join(' '); card.dataset.available = String(group.available);
    const imageLink = element('a', 'gbm-shop-card__image'); imageLink.href = relativeURL(primary.url);
    imageLink.dataset.cardLink = ''; imageLink.setAttribute('aria-label', `View ${group.name}`);
    imageLink.append(imageOrPlaceholder(primary.image, group.name, 'gbm-card-image'));
    const heading = element('div', 'gbm-shop-card__heading');
    const title = element('h2'); const titleLink = element('a', '', group.name);
    titleLink.href = relativeURL(primary.url); titleLink.dataset.cardLink = ''; title.append(titleLink);
    const lowest = group.lowest || primary;
    const varies = group.choices.some(choice => choice.varies || choice.price !== lowest.price);
    const priceText = (varies && !lowest.priceText.startsWith('From ') ? 'From ' : '') + lowest.priceText;
    const price = element('p', '', priceText); price.dataset.cardPrice = ''; heading.append(title, price);
    const format = element('p', 'gbm-shop-card__format', group.selected.capsules && group.selected.powder ? 'Capsules & powder' :
      group.selected.capsules ? 'Capsules' : group.selected.powder ? 'Powder' : 'Mushroom product'); format.dataset.cardFormat = '';
    const availability = element('p', 'gbm-card-availability', 'Sold out');
    availability.dataset.cardAvailability = ''; availability.hidden = group.available;
    const choices = element('div', 'gbm-card-formats'); choices.setAttribute('aria-label', `Choose a format for ${group.name}`);
    group.formats.forEach(format => {
      const choice = group.selected[format];
      const link = element('a', '', format === 'capsules' ? 'Capsules' : format === 'powder' ? 'Powder' : 'View product');
      link.href = relativeURL(choice.url); link.dataset.formatLink = format; link.dataset.price = choice.priceText;
      link.dataset.image = choice.image || ''; link.dataset.available = String(choice.available); choices.append(link);
    });
    card.append(imageLink, heading, format, availability, choices);
    if (group.extras.length) {
      const details = element('details', 'gbm-more-options'); details.dataset.extraOptions = '';
      details.append(element('summary', '', 'More size options'));
      group.extras.forEach(choice => {
        const link = element('a', '', `${choice.title} — ${choice.priceText}${choice.available ? '' : ' · Sold out'}`);
        link.href = relativeURL(choice.url); link.dataset.extraFormat = choice.format; details.append(link);
      });
      card.append(details);
    }
    return card;
  }
  function makeTile(group, source, index) {
    const node = element('a', 'gbm-mushroom-card'); node.dataset.mushroomKey = group.key;
    node.href = mushroomURL(source, group.key);
    const picture = element('div', `gbm-mushroom-card__image gbm-tone-${(index + 1) % 5}`);
    const product = group.products.find(product => product.mushroomImage) || group.products[0];
    picture.append(imageOrPlaceholder(product?.mushroomImage, group.name, ''));
    const label = element('div', 'gbm-mushroom-card__label'); label.append(element('h3', '', group.name), arrow());
    node.append(picture, label); return node;
  }
  function enhance(root = document) {
    $$ (root, '[data-gbm-nav], [data-gbm-mushrooms]').forEach(node => {
      const url = node.dataset.catalogUrl;
      if (!url) return;
      ready(url).then(catalog => {
        if (!node.isConnected) return;
        const fragment = document.createDocumentFragment();
        if (node.hasAttribute('data-gbm-mushrooms')) {
          // Keep merchant image/link overrides and Theme Editor block attributes.
          const old = new Map($$(node, '[data-mushroom-key]').map(tile => [tile.dataset.mushroomKey, tile]));
          catalog.groups.forEach((group, index) => fragment.append(old.get(group.key) || makeTile(group, url, index)));
        } else {
          catalog.groups.forEach(group => {
            const link = element('a', '', group.name); link.href = mushroomURL(url, group.key);
            if (node.classList.contains('gbm-guide-index')) link.append(arrow());
            fragment.append(link);
          });
        }
        node.replaceChildren(fragment);
      }).catch(error => {
        // Server-rendered cards, menu entries and the Shop all link stay usable.
        node.dataset.catalogIncomplete = 'true';
        console.warn('The remaining catalog pages could not be loaded.', error);
      });
    });
  }
  window.GbmDiscovery = {ready, groupProducts, makeCard, key, enhance};
  register(); enhance();
  document.addEventListener('shopify:section:load', event => { register(event.target, true); enhance(event.target); });
})();
